import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { 
    Coins, Calendar, Check, Star, Heart, Users, 
    Palette, Sparkles, Compass, Music, ChevronRight,
    Gift, ArrowRight, Crown
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
    { id: 'move_well', icon: Heart, name: 'Move Well', description: 'Yoga, tai chi, walking groups, balance classes', color: 'emerald' },
    { id: 'connect_well', icon: Users, name: 'Connect Well', description: 'Coffee mornings, book clubs, quiz afternoons', color: 'blue' },
    { id: 'create_well', icon: Palette, name: 'Create Well', description: 'Art classes, floristry, pottery, crafts', color: 'purple' },
    { id: 'feel_well', icon: Sparkles, name: 'Feel Well', description: 'Meditation, sound healing, mindfulness', color: 'amber' },
    { id: 'explore_well', icon: Compass, name: 'Explore Well', description: 'Day trips, garden visits, cultural outings', color: 'cyan' },
    { id: 'celebrate_well', icon: Music, name: 'Celebrate Well', description: 'Live music, seasonal events, celebrations', color: 'rose' },
];

const CREDIT_EXAMPLES = [
    { credits: 1, examples: 'Yoga, coffee socials, book clubs, film afternoons' },
    { credits: 2, examples: 'Sound healing, workshops, art classes, piano concerts' },
    { credits: 3, examples: 'Lunch outings, museum visits, special events' },
    { credits: '4-5', examples: 'Full-day excursions, theatre trips, gala events' },
];

export function ResidentsClubPage() {
    const navigate = useNavigate();
    const { isAuthenticated, profile } = useAuth();
    const [membershipInfo, setMembershipInfo] = useState(null);

    useEffect(() => {
        fetchMembershipInfo();
    }, []);

    const fetchMembershipInfo = async () => {
        try {
            const response = await axios.get(`${API}/membership-info`);
            setMembershipInfo(response.data);
        } catch (error) {
            console.error('Error fetching membership info:', error);
        }
    };

    const isActiveMember = profile?.membership_status === 'active';
    const tiers = membershipInfo?.tiers;

    return (
        <div className="min-h-screen bg-stone-50" data-testid="residents-club-page">
            {/* Hero Section */}
            <div className="bg-gradient-to-br from-emerald-800 via-emerald-700 to-teal-800 text-white">
                <PageHeader title="" showBack className="text-white" />
                
                <div className="px-5 pb-10 text-center">
                    <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                        <Star className="w-10 h-10" />
                    </div>
                    <h1 className="text-3xl font-bold mb-2">Residents Club</h1>
                    <p className="text-emerald-100 text-lg">Lifestyle & Wellbeing Membership</p>
                </div>
            </div>
            
            <div className="px-5 py-8">
                {/* Membership Tiers */}
                <div className="mb-10 -mt-8">
                    <div className="grid md:grid-cols-2 gap-4">
                        {/* Essential Tier */}
                        <div className="bg-white rounded-2xl border-2 border-stone-200 p-5 shadow-sm">
                            <div className="text-center mb-4">
                                <Star className="w-8 h-8 text-stone-400 mx-auto mb-2" />
                                <h3 className="text-xl font-bold text-stone-900">Essential</h3>
                                <p className="text-stone-500 text-sm">Perfect for occasional participation</p>
                            </div>
                            
                            <div className="text-center mb-4">
                                <div className="flex items-baseline justify-center gap-1">
                                    <span className="text-4xl font-bold text-stone-900">£{tiers?.essential?.price || 499}</span>
                                    <span className="text-stone-500">/year</span>
                                </div>
                                <p className="text-emerald-600 font-semibold mt-1">
                                    {tiers?.essential?.credits || 15} credits included
                                </p>
                            </div>
                            
                            <ul className="space-y-2 mb-4">
                                {(tiers?.essential?.features || [
                                    '15 activity credits per year',
                                    'Access to all 6 wellness categories',
                                    'Book any activity',
                                    'Digital membership card',
                                    'Calendar sync'
                                ]).map((feature, idx) => (
                                    <li key={idx} className="flex items-start gap-2 text-sm text-stone-600">
                                        <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                                        {feature}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        
                        {/* Premium Tier */}
                        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl border-2 border-emerald-400 p-5 shadow-md relative">
                            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                <span className="bg-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                                    MOST POPULAR
                                </span>
                            </div>
                            
                            <div className="text-center mb-4 mt-2">
                                <Crown className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                                <h3 className="text-xl font-bold text-stone-900">Premium</h3>
                                <p className="text-stone-500 text-sm">Best value for active members</p>
                            </div>
                            
                            <div className="text-center mb-4">
                                <div className="flex items-baseline justify-center gap-1">
                                    <span className="text-4xl font-bold text-emerald-700">£{tiers?.premium?.price || 995}</span>
                                    <span className="text-stone-500">/year</span>
                                </div>
                                <p className="text-emerald-600 font-semibold mt-1">
                                    {tiers?.premium?.credits || 30} credits included
                                </p>
                            </div>
                            
                            <ul className="space-y-2 mb-4">
                                {(tiers?.premium?.features || [
                                    '30 activity credits per year',
                                    'Access to all 6 wellness categories',
                                    'Book any activity',
                                    'Digital membership card',
                                    'Calendar sync',
                                    'Best value for regular participants'
                                ]).map((feature, idx) => (
                                    <li key={idx} className="flex items-start gap-2 text-sm text-stone-700">
                                        <Check className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                                        {feature}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Philosophy Section */}
                <div className="mb-10">
                    <h2 className="text-xl font-bold text-stone-900 mb-3">More Than Activities</h2>
                    <p className="text-stone-600 leading-relaxed">
                        The Burston Residents Lifestyle Membership is a community-led programme designed to help residents stay socially connected, maintain confidence and wellbeing, and enjoy meaningful experiences throughout the year.
                    </p>
                </div>
                
                {/* How Credits Work */}
                <div className="mb-10">
                    <h2 className="text-xl font-bold text-stone-900 mb-4">How Credits Work</h2>
                    
                    <div className="bg-white rounded-2xl border border-stone-200 p-5 mb-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                                <Coins className="w-6 h-6 text-amber-600" />
                            </div>
                            <div>
                                <p className="font-bold text-stone-900">Flexible Credit System</p>
                                <p className="text-sm text-stone-500">Use your credits however you like</p>
                            </div>
                        </div>
                        
                        <p className="text-stone-600 text-sm mb-4">
                            Activities are priced 1-5 credits based on their nature. Use your credits however you like—attend regularly or save for special experiences.
                        </p>
                        
                        <div className="space-y-3">
                            {CREDIT_EXAMPLES.map((item, idx) => (
                                <div key={idx} className="flex gap-3">
                                    <span className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-sm flex-shrink-0">
                                        {item.credits}
                                    </span>
                                    <p className="text-sm text-stone-600">{item.examples}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-4">
                        <p className="text-emerald-800 text-sm">
                            <strong>Need more?</strong> Purchase additional credit packs anytime: 5 credits for £75 or 10 credits for £140.
                        </p>
                    </div>
                </div>
                
                {/* Experience Categories */}
                <div className="mb-10">
                    <h2 className="text-xl font-bold text-stone-900 mb-4">Six Pillars of Wellbeing</h2>
                    
                    <div className="grid grid-cols-2 gap-3">
                        {CATEGORIES.map((cat) => {
                            const Icon = cat.icon;
                            const colorClasses = {
                                emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
                                blue: 'bg-blue-50 border-blue-200 text-blue-700',
                                purple: 'bg-purple-50 border-purple-200 text-purple-700',
                                amber: 'bg-amber-50 border-amber-200 text-amber-700',
                                cyan: 'bg-cyan-50 border-cyan-200 text-cyan-700',
                                rose: 'bg-rose-50 border-rose-200 text-rose-700',
                            };
                            return (
                                <div key={cat.id} className={`rounded-xl border p-4 ${colorClasses[cat.color]}`}>
                                    <Icon className="w-6 h-6 mb-2" />
                                    <h4 className="font-bold text-sm">{cat.name}</h4>
                                    <p className="text-xs opacity-80 mt-1">{cat.description}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
                
                {/* What's Included */}
                <div className="mb-10">
                    <h2 className="text-xl font-bold text-stone-900 mb-4">Both Tiers Include</h2>
                    
                    <div className="bg-white rounded-2xl border border-stone-200 p-5">
                        <ul className="space-y-3">
                            {[
                                'Access to the full resident programme calendar',
                                'Priority booking for popular sessions',
                                'Member pricing on premium experiences',
                                'Free community events and gatherings',
                                'Ability to purchase additional credits',
                                'Waitlist access for fully-booked activities',
                                'Calendar sync for all your bookings'
                            ].map((item, idx) => (
                                <li key={idx} className="flex items-start gap-3">
                                    <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                                    <span className="text-stone-700">{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
                
                {/* Community Events */}
                <div className="mb-10">
                    <h2 className="text-xl font-bold text-stone-900 mb-3">Free Community Events</h2>
                    <p className="text-stone-600 text-sm mb-4">
                        Some gatherings are included outside the credit system to foster community spirit:
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {['Welcome coffees', 'Resident forums', 'Launch events', 'Information talks', 'Selected celebrations'].map((item, idx) => (
                            <span key={idx} className="bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-full text-sm">
                                {item}
                            </span>
                        ))}
                    </div>
                </div>
                
                {/* CTA */}
                <div className="pb-8">
                    {isActiveMember ? (
                        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-6 text-white text-center">
                            <Gift className="w-10 h-10 mx-auto mb-3 opacity-90" />
                            <h3 className="font-bold text-lg mb-2">You're a Member!</h3>
                            <p className="text-emerald-100 text-sm mb-4">
                                You have <strong>{profile?.credits_balance || 0} credits</strong> available
                            </p>
                            <Button
                                onClick={() => navigate('/residents/dashboard')}
                                className="bg-white text-emerald-700 hover:bg-emerald-50 font-semibold"
                            >
                                Go to My Dashboard <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </div>
                    ) : (
                        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-6 text-white text-center">
                            <h3 className="font-bold text-lg mb-2">Ready to Join?</h3>
                            <p className="text-emerald-100 text-sm mb-4">
                                Contact our membership team to get started with your Burston Residents Lifestyle Membership.
                            </p>
                            <div className="space-y-2">
                                <Button
                                    onClick={() => navigate(isAuthenticated ? '/member' : '/login')}
                                    className="w-full bg-white text-emerald-700 hover:bg-emerald-50 font-semibold"
                                >
                                    {isAuthenticated ? 'View My Account' : 'Sign In / Register'}
                                </Button>
                                <p className="text-emerald-200 text-xs">
                                    Or speak to reception: 01onal 456789
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ResidentsClubPage;
